<template>
  <div class="agreements-consent-block">
    <SvoiCheckbox v-model="requiredValue" :error="requiredErrorText" class="confirm">
      Даю своё согласие на обработку
      <a :href="linkFor('privacy_accepted')" target="_blank" rel="noopener" class="link" @click.stop>
        персональных данных
      </a>
      в соответствии с ФЗ «О персональных данных» и
      <a :href="linkFor('policy_accepted')" target="_blank" rel="noopener" class="link" @click.stop>
        политикой обработки персональных данных
      </a>
      АО «Россельхозбанк» и подтверждаю своё согласие с
      <a :href="linkFor('terms_accepted')" target="_blank" rel="noopener" class="link" @click.stop>правилами</a>
    </SvoiCheckbox>

    <SvoiCheckbox v-if="hasMarketing" v-model="marketingValue" class="confirm">
      Даю своё согласие на информирование о
      <a :href="linkFor('marketing_accepted')" target="_blank" rel="noopener" class="link" @click.stop>
        продуктах, услугах, сервисах Банка
      </a>
    </SvoiCheckbox>
  </div>
</template>

<script lang="ts" setup>
import { SvoiCheckbox } from '@svoi-ui/components/checkbox'
import type { AgreementsDocument } from '~/models/agreements'
import { AGREEMENTS_FALLBACK_LINKS } from '~/shared/const/agreements'
import type { AgreementsDocumentsCode } from '~/shared/types/agreements'

const props = withDefaults(
  defineProps<{
    codes: Array<AgreementsDocumentsCode>
    requiredError?: string | Ref<string>
  }>(),
  { requiredError: '' },
)

const emit = defineEmits<{
  (e: 'change', versionIds: Array<string>): void
}>()

const requiredValue = defineModel<boolean>('required', { default: false })
const marketingValue = defineModel<boolean>('marketing', { default: false })

const { $services } = useNuxtApp()
const { isAuth } = useAuth()

const documents = ref<Array<AgreementsDocument>>([])

const hasMarketing = computed(() => props.codes.includes('marketing_accepted'))

const requiredErrorText = computed(() => unref(props.requiredError))

const selectedVersionIds = computed(() =>
  documents.value
    .filter(document => (document.isRequired ? requiredValue.value : marketingValue.value))
    .map(document => document.actualVersion?.id ?? '')
    .filter(Boolean),
)

function linkFor(code: AgreementsDocumentsCode): string {
  const document = documents.value.find(item => item.code === code)

  return document?.actualVersion?.sourceLink || AGREEMENTS_FALLBACK_LINKS[code] || ''
}

watch(selectedVersionIds, versionIds => emit('change', versionIds))

onMounted(async () => {
  try {
    const actualDocuments = await $services.agreementsService.documents.getActualDocuments()
    documents.value = actualDocuments.filter(document => props.codes.includes(document.code as AgreementsDocumentsCode))
  }
  catch (error) {
    console.warn('[AgreementsConsentBlock] Failed to load documents:', error)
    return
  }

  if (!isAuth.value || !documents.value.length) {
    return
  }

  try {
    const signatures = await $services.agreementsService.signatures.getListModel({
      include: 'document,version',
    })

    const signedVersionIds = new Set(
      signatures.map(s => s.version?.id).filter(Boolean) as Array<string>,
    )

    const requiredDocs = documents.value.filter(d => d.isRequired)
    const marketingDocs = documents.value.filter(d => !d.isRequired)

    if (requiredDocs.length && requiredDocs.every(d => signedVersionIds.has(d.actualVersion?.id ?? ''))) {
      requiredValue.value = true
    }

    if (marketingDocs.some(d => signedVersionIds.has(d.actualVersion?.id ?? ''))) {
      marketingValue.value = true
    }
  }
  catch (error) {
    console.warn('[AgreementsConsentBlock] Не удалось проверить существующие подписи:', error)
  }
})
</script>

<style scoped lang="scss">
.agreements-consent-block {
  @extend %flex-column;

  gap: 16px;
  width: 100%;
  max-width: 560px;

  > .confirm {
    gap: 8px;
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;

    .link {
      display: inline;
      cursor: pointer;
      color: $color-kiwi-50;
    }
  }
}
</style>
